import type {
    IFormFieldValue,
    IResponseTiming,
    IResponseTimingInput,
} from "@agrigate/survey-api-def/lib/SurveysService/dto";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { type ReactElement, useCallback } from "react";
import type { SurveyQuestion } from "./SurveyBuilderForm";
import type { SurveyResponse } from "./SurveyFormGenerator";
import SurveyRunnerShell from "./SurveyRunnerShell";
import type { SurveyLink } from "./utils/survey-config-serde";
import useGraphMultiStepForm from "./utils/useGraphMultiStepForm";

export interface GraphSurveyConfig {
    questions: SurveyQuestion[];
    links: SurveyLink[];
    entryQuestionId?: string;
}

const GraphSurveyFormGenerator = (p: {
    surveyId: string;
    formName: string;
    config: GraphSurveyConfig;
    response?: MaybeN<IFormFieldValue[]>;
    timing?: MaybeN<IResponseTiming>;
    rewardCoins?: MaybeN<number>;
    onSubmit: (
        response: SurveyResponse[],
        timing: IResponseTimingInput,
        isComplete?: MaybeN<boolean>,
    ) => void;
    assetBaseUrl: string;
}): ReactElement => {
    const { formName, config, response, timing, rewardCoins, onSubmit } = p;
    const {
        step,
        isFirstStep,
        isLastStep,
        next,
        back,
        answers,
        doneCount,
        estimatedTotal,
        progressValue,
        canAdvance,
        readTiming,
        path,
    } = useGraphMultiStepForm(config, response, timing);

    const handleSave = useCallback(
        (isComplete?: MaybeN<boolean>) => {
            const data: SurveyResponse[] = path.map(id => ({
                id,
                value: answers[id] ?? "",
            }));
            onSubmit(data, readTiming(), isComplete);
        },
        [path, answers, onSubmit, readTiming],
    );

    return (
        <SurveyRunnerShell
            formName={formName}
            step={step}
            progressValue={progressValue}
            progressLabel={`${doneCount} of ~${estimatedTotal}`}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            canAdvance={canAdvance}
            rewardCoins={rewardCoins}
            onBack={back}
            onNext={() => {
                handleSave();
                next();
            }}
            onSubmit={() => handleSave(true)}
            onFlush={() => handleSave()}
        />
    );
};

export default GraphSurveyFormGenerator;
